import { addDays, isoWeekday } from './dates.js';
import { type HolidayRegion, holidayOn } from './holidays.js';

// Working days: Monday to Friday without public holidays of the region (GANTT-07). Individual
// working hours per weekday (part time) are handled by the capacity module, not here.

export function isWeekend(date: string): boolean {
  return isoWeekday(date) >= 6;
}

export function isWorkingDay(date: string, region: HolidayRegion): boolean {
  return !isWeekend(date) && holidayOn(date, region) === null;
}

/** Working days from `from` to `to`, both inclusive; 0 if `to` is before `from`. */
export function countWorkingDays(from: string, to: string, region: HolidayRegion): number {
  let count = 0;
  for (let d = from; d <= to; d = addDays(d, 1)) {
    if (isWorkingDay(d, region)) count++;
  }
  return count;
}

/**
 * Moves `date` by `days` working days (negative = backwards). Counting starts from `date`
 * itself, which need not be a working day: `addWorkingDays(friday, 1)` is the next Monday,
 * `addWorkingDays(saturday, 0)` stays Saturday.
 */
export function addWorkingDays(date: string, days: number, region: HolidayRegion): string {
  const step = days < 0 ? -1 : 1;
  let remaining = Math.abs(days);
  let d = date;
  while (remaining > 0) {
    d = addDays(d, step);
    if (isWorkingDay(d, region)) remaining--;
  }
  return d;
}

/** `date` itself if it is a working day, otherwise the next (or previous) working day. */
export function nearestWorkingDay(
  date: string,
  region: HolidayRegion,
  direction: 'forward' | 'backward' = 'forward',
): string {
  const step = direction === 'forward' ? 1 : -1;
  let d = date;
  while (!isWorkingDay(d, region)) d = addDays(d, step);
  return d;
}
